import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { useRealtime, usePolling, fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChefHat, ShoppingBag, MonitorPlay, LayoutDashboard, MapPin, Truck, LogOut, Bell, Wifi, WifiOff, ShieldCheck, Volume2, VolumeX, Clock, PlayCircle, StopCircle, Users, Building2, BarChart3, MessageSquareHeart, Megaphone, ShieldAlert, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { OrderTypeBadge } from '@/components/layout/StaffRail';
import { unlockAudio, playAdminPing } from '@/lib/chime';

/**
 * Server Console - the central screen the restaurant operator uses on the in-house server/laptop.
 * Aggregates KPIs + active queue + new-order alerts. Designed to be left running all day.
 */
export default function ServerConsole() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  // Only owners can switch between branches. Every other role is pinned
  // to their assigned branch (single-store visibility).
  const canSwitchBranch = user?.role === 'owner';
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [soundOn, setSoundOn] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [shiftStatus, setShiftStatus] = useState(null);
  const [feedbackAlerts, setFeedbackAlerts] = useState(0);

  useEffect(() => {
    api.get('/branches').then((r) => {
      const list = r.data || [];
      setBranches(list);
      // Non-owners are forced to their own branch even if a stale id was
      // previously selected. Owners default to their own branch on first
      // load but can switch via the dropdown.
      setBranchId((prev) => {
        if (!canSwitchBranch && user?.branch_id) return user.branch_id;
        return prev || user?.branch_id || list?.[0]?.id || '';
      });
    });
    api.get('/staff/attendance/me/status').then((r) => setShiftStatus(r.data)).catch(() => {});
    if (['admin', 'owner'].includes(user?.role)) {
      api.get('/feedback/alerts').then((r) => setFeedbackAlerts(r.data?.count || 0)).catch(() => setFeedbackAlerts(0));
    }
  }, [user, canSwitchBranch]);

  // Unlock audio on the first user interaction so the alert chime can play
  useEffect(() => {
    const handler = () => { unlockAudio(); };
    window.addEventListener('pointerdown', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const refreshShift = async () => {
    try { const { data } = await api.get('/staff/attendance/me/status'); setShiftStatus(data); } catch (e) { /* noop */ }
  };
  const clockIn = async () => { try { await api.post('/staff/attendance/clock-in'); toast.success('Clocked in'); refreshShift(); } catch (e) { toast.error('Failed'); } };
  const clockOut = async () => { try { await api.post('/staff/attendance/clock-out'); toast.success('Clocked out'); refreshShift(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  const reload = useCallback(async () => {
    if (!branchId) return;
    try {
      const [s, a] = await Promise.all([
        api.get('/admin/stats/overview', { params: { branch_id: branchId } }),
        api.get('/orders/active', { params: { branch_id: branchId } }),
      ]);
      setStats(s.data);
      setActive(a.data || []);
    } catch (e) { /* noop */ }
  }, [branchId]);

  useEffect(() => { reload(); }, [reload]);

  const { connected } = useRealtime(
    branchId ? [`admin:${branchId}`] : [],
    useCallback((msg) => {
      if (msg?.type === 'order_created' && soundOn) {
        playAdminPing();
        toast(`🔔 New order #${msg.order?.order_number}`);
      }
      reload();
    }, [reload, soundOn]),
  );
  useEffect(() => setWsConnected(connected), [connected]);
  usePolling(reload, 8000, [branchId]);

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Server console</p>
            <h1 className="font-display text-xl font-semibold">{branches.find((b) => b.id === branchId)?.name || 'City Grill'}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="server-ws-status" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${wsConnected ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
              {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />} {wsConnected ? 'Live' : 'Polling'}
            </span>
            {canSwitchBranch ? (
              <select data-testid="server-branch-select" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            ) : (
              <span data-testid="server-branch-lock" className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-muted/40 px-3 text-xs text-muted-foreground" title="Only owners can switch branches.">
                {branches.find((b) => b.id === branchId)?.name || '\u2014'}
              </span>
            )}
            <Button
              data-testid="server-sound-toggle"
              size="sm"
              variant={soundOn ? 'default' : 'secondary'}
              onClick={async () => {
                const next = !soundOn;
                setSoundOn(next);
                if (next) {
                  await unlockAudio();
                  playAdminPing();
                  toast.success('Alerts enabled');
                } else {
                  toast('Alerts muted');
                }
              }}
            >
              {soundOn ? <Volume2 className="mr-1 h-4 w-4" /> : <VolumeX className="mr-1 h-4 w-4" />} Alerts
            </Button>
            <Button data-testid="server-logout-button" size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] grid gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          {/* KPI strip */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Sales today" value={fmtMoney(stats?.total_sales || 0)} accent="text-primary" />
            <Kpi label="Paid sales" value={fmtMoney(stats?.paid_sales || 0)} accent="text-emerald-700" />
            <Kpi label="Orders today" value={stats?.order_count ?? '-'} />
            <Kpi label="Active now" value={stats?.active_orders ?? '-'} accent="text-primary" highlight />
          </div>

          {/* Active orders board */}
          <Card>
            <div className="flex items-center justify-between border-b border-border p-4">
              <h2 className="font-display text-lg font-semibold">Active orders</h2>
              <Badge variant="secondary">{active.length}</Badge>
            </div>
            <ScrollArea className="max-h-[60vh]">
              <ul className="divide-y divide-border">
                {active.map((o) => (
                  <li key={o.id} data-testid="server-active-order" className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-display text-lg font-semibold">#{o.order_number}</span>
                        <OrderTypeBadge orderType={o.order_type} tableNumber={o.table_number} />
                        <Badge className={`border ${ORDER_STATUS_COLORS[o.status] || ''}`}>{getContextualStatusLabel(o.status, o.order_type, o.table_number)}</Badge>
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {o.customer_name || 'Guest'} · {(o.items || []).slice(0, 3).map((i) => `${i.quantity}× ${i.name}`).join(', ')}{(o.items || []).length > 3 ? `…` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm tabular text-muted-foreground">{fmtTimeAgo(o.created_at)}</span>
                      <span className="text-sm font-semibold tabular">{fmtMoney(o.total)}</span>
                      <Button size="sm" variant="secondary" onClick={() => nav(`/order/${o.id}`)}>View</Button>
                    </div>
                  </li>
                ))}
                {!active.length && <li className="p-8 text-center text-sm text-muted-foreground">No active orders.</li>}
              </ul>
            </ScrollArea>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <div className="border-b border-border p-4">
              <h2 className="font-display text-lg font-semibold">Quick launch</h2>
              <p className="text-xs text-muted-foreground">Open a screen for each station</p>
            </div>
            <div className="grid grid-cols-2 gap-2 p-3">
              <LaunchTile data-testid="server-launch-kds" icon={<ChefHat className="h-5 w-5" />} label="Kitchen Display" subtitle="For the kitchen iPad/screen" onClick={() => nav('/kds')} />
              <LaunchTile data-testid="server-launch-pos" icon={<MonitorPlay className="h-5 w-5" />} label="POS" subtitle="For the cashier" onClick={() => nav('/pos')} />
              <LaunchTile data-testid="server-launch-admin" icon={<LayoutDashboard className="h-5 w-5" />} label="Admin" subtitle="Reports & menu" onClick={() => nav('/admin')} />
              <LaunchTile data-testid="server-launch-menu" icon={<ShoppingBag className="h-5 w-5" />} label="Customer menu" subtitle="Show on a guest device" onClick={() => nav('/menu')} />
              <LaunchTile data-testid="server-launch-staff" icon={<Users className="h-5 w-5" />} label="Staff" subtitle="Members & attendance" onClick={() => nav('/staff')} />
              <LaunchTile data-testid="server-launch-branches" icon={<Building2 className="h-5 w-5" />} label="Branches" subtitle="Multi-branch setup" onClick={() => nav('/branches-admin')} />
              <LaunchTile data-testid="server-launch-reports" icon={<BarChart3 className="h-5 w-5" />} label="Reports" subtitle="Sales & exports" onClick={() => nav('/reports')} />
              <LaunchTile data-testid="server-launch-feedback" icon={<MessageSquareHeart className="h-5 w-5" />} label={`Feedback${feedbackAlerts ? ` (${feedbackAlerts})` : ''}`} subtitle="Customer ratings" onClick={() => nav('/feedback-admin')} />
              <LaunchTile data-testid="server-launch-marketing" icon={<Megaphone className="h-5 w-5" />} label="Marketing" subtitle="SMS & birthday" onClick={() => nav('/marketing')} />
              <LaunchTile data-testid="server-launch-inventory" icon={<Boxes className="h-5 w-5" />} label="Inventory" subtitle="Stock & recipes" onClick={() => nav('/inventory')} />
              <LaunchTile data-testid="server-launch-audit" icon={<ShieldAlert className="h-5 w-5" />} label="Audit log" subtitle="Sensitive events" onClick={() => nav('/audit')} />
              <LaunchTile data-testid="server-launch-checkin" icon={<Clock className="h-5 w-5" />} label="Kiosk" subtitle="Building entrance check-in" onClick={() => window.open('/checkin', '_blank')} />
            </div>
          </Card>

          <Card>
            <div className="border-b border-border p-4">
              <h2 className="font-display text-lg font-semibold">My shift</h2>
              <p className="text-xs text-muted-foreground">Clock in/out for attendance</p>
            </div>
            <div className="p-4">
              {shiftStatus?.is_clocked_in ? (
                <>
                  <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-900"><Clock className="mr-1 h-3 w-3" /> On shift</Badge>
                  <p className="mt-2 text-xs text-muted-foreground">Since {fmtTimeAgo(shiftStatus.shift?.clock_in)}</p>
                  <Button data-testid="server-clock-out-button" size="sm" className="mt-3 w-full" variant="secondary" onClick={clockOut}><StopCircle className="mr-1 h-4 w-4" /> Clock out</Button>
                </>
              ) : (
                <>
                  <Badge variant="secondary">Off shift</Badge>
                  <Button data-testid="server-clock-in-button" size="sm" className="mt-3 w-full" onClick={clockIn}><PlayCircle className="mr-1 h-4 w-4" /> Clock in</Button>
                </>
              )}
            </div>
          </Card>

          <Card>
            <div className="border-b border-border p-4">
              <h2 className="font-display text-lg font-semibold">Install as app</h2>
              <p className="text-xs text-muted-foreground">Run this in fullscreen kiosk mode</p>
            </div>
            <div className="space-y-2 p-4 text-sm">
              <p>On an <strong>iPad</strong>: tap Share → <em>Add to Home Screen</em>. The icon opens this app full-screen with no browser bar.</p>
              <p>On a <strong>Mac/Windows</strong>: in Chrome/Edge, click the install icon in the address bar.</p>
              <p className="inline-flex items-center gap-1 text-xs text-emerald-800"><ShieldCheck className="h-3.5 w-3.5" /> Secure HTTPS connection</p>
            </div>
          </Card>

          <Card>
            <div className="border-b border-border p-4">
              <h2 className="font-display text-lg font-semibold">Signed in as</h2>
            </div>
            <div className="p-4 text-sm">
              <p className="font-semibold">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </Card>
        </aside>
      </main>
    </div>
  );
}

function Kpi({ label, value, accent = '', highlight = false }) {
  return (
    <Card className={highlight ? 'ring-2 ring-primary/20' : ''}>
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`mt-1 font-display text-2xl font-semibold tabular ${accent}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function LaunchTile({ icon, label, subtitle, onClick, ...rest }) {
  return (
    <button {...rest} onClick={onClick} className="group flex flex-col items-start gap-1 rounded-lg border border-border bg-card p-3 text-left transition-all hover:border-primary/40 hover:bg-secondary">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground">{icon}</span>
      <p className="mt-1 text-sm font-semibold">{label}</p>
      <p className="text-[11px] text-muted-foreground">{subtitle}</p>
    </button>
  );
}
